import React, { useEffect, useState, useCallback } from "react";
import Grid from "@mui/material/Grid";
import MDBox from "components/MDBox";
import IntegrationInfoCard from "./IntegrationInfoCard";

import CreateBallparc from "./components/Ballparc";

import CreateOmnipark from "./components/Omnipark";

import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import {
  getPlaceById,
  updatePlace,
  setSelectedPlace,
} from "store/slice/places/placeSlice";
import { get } from "lodash";
import MDSnackbar from "components/MDSnackbar";
import CircularIndeterminate from "components/MDLoading";
import { useMaterialUIController } from "context";
import ConfirmationDialog from "components/UIComponents/ConfirmationDialog";

const Integrations = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const [controller] = useMaterialUIController();
  const { sidenavColor } = controller;

  const [dialogOpen, setDialogOpen] = useState(false);
  const [isEditBallparc, setIsEditBallparc] = useState(false);

  const [dialogOpenOmnipark, setDialogOpenOmnipark] = useState(false);
  const [isEditOmnipark, setIsEditOmnipark] = useState(false);
  const [confirmationDialog, setConfirmationDialog] = useState({
    open: false,
    title: "",
    description: "",
    onConfirm: null,
  });

  const openConfirmation = (title, description, onConfirm) => {
    setConfirmationDialog({
      open: true,
      title,
      description,
      onConfirm,
    });
  };

  const [isLoading, setIsLoading] = useState(false);
  const [placeData, setPlaceData] = useState({});

  const [notification, setNotification] = useState({
    color: "",
    icon: "",
    title: "",
    content: "",
    show: false,
    close: () => setNotification((prev) => ({ ...prev, show: false })),
  });

  const selectedPlace = useSelector((state) => state.places?.selectedPlace);
  const placeId =
    selectedPlace?._id || new URLSearchParams(location?.search).get("placeId");

  const getPlaceData = useCallback(() => {
    dispatch(getPlaceById(placeId))
      .unwrap()
      .then((res) => {
        if (res?.success) setPlaceData(res?.place);
      });
  }, [dispatch, placeId]);

  useEffect(() => {
    if (selectedPlace?._id) setPlaceData(selectedPlace);
    else getPlaceData();
  }, [selectedPlace, getPlaceData]);

  useEffect(() => {
    if (!dialogOpen && !dialogOpenOmnipark) getPlaceData();
  }, [dialogOpen, dialogOpenOmnipark, getPlaceData]);

  const handleIntegrationToggle = useCallback(
    (type, rest) => {
      const currentSetup = selectedPlace?.setup || {};
      const currentSteps = currentSetup?.steps || {};
      const payload = {
        ...rest,
        setup: {
          ...currentSetup,
          steps: {
            ...currentSteps,
            generalSettings: true,
          },
        },
        type,
        placeId,
      };
      setIsLoading(true);
      dispatch(updatePlace(payload))
        .unwrap()
        .then(async (res) => {
          const success = res?.success;
          setIsLoading(false);

          setNotification({
            color: success ? "success" : "error",
            title: success ? "Success" : "Error",
            content: res?.message,
            icon: success ? "check" : "warning",
            show: true,
          });

          if (success) {
            getPlaceData();
            await dispatch(setSelectedPlace(res?.updatedPlaceData));
          }
        })
        .catch((err) => {
          setIsLoading(false);
          setNotification({
            color: "error",
            title: "Error",
            content: err?.message,
            icon: "warning",
            show: true,
          });
        });
    },
    [dispatch, placeId, getPlaceData],
  );

  const handleNotificationClose = useCallback(() => {
    setNotification((prev) => ({ ...prev, show: false }));
  }, []);

  return (
    <MDBox mb={2}>
      <Grid container alignItems="center" spacing={4} py={2}>
        {[
          /*************** Ballparc ***************/
          {
            icon: "business",
            title: "Ballparc",
            description:
              "Connects to the Ballparc parking platform using an API key, API secret, and location ID to sync parking session data between Dock and Ballparc.",
            isEnable: get(placeData, "ballparc.isEnable", false),
            onToggle: () => {
              if (get(placeData, "ballparc.isEnable")) {
                openConfirmation(
                  "Ballparc Disable Confirmation!",
                  "Are you sure you want to Disable Ballparc?",
                  () =>
                    handleIntegrationToggle("UPDATE", {
                      ballparc: {
                        locationId: "",
                        apiKey: "",
                        apiSecret: "",
                        isEnable: false,
                      },
                    }),
                );
              } else {
                setIsEditBallparc(false);
                setDialogOpen(true);
              }
            },
            onEdit: () => {
              setIsEditBallparc(true);
              setDialogOpen(true);
            },
            showEdit:
              get(placeData, "ballparc.isEnable") &&
              get(placeData, "ballparc.apiKey") &&
              get(placeData, "ballparc.apiSecret") &&
              get(placeData, "ballparc.locationId"),
          },

          /***************  Omnipark ***************/
          {
            icon: "local_parking",
            title: "Omnipark",
            description:
              "Connects to Omnipark's parking management system using a location ID, enabling Dock to share parking session data with the Omnipark platform.",
            isEnable: get(placeData, "omnipark.isEnable", false),
            onToggle: () => {
              if (get(placeData, "omnipark.isEnable")) {
                openConfirmation(
                  "Omnipark Disable Confirmation!",
                  "Are you sure you want to Disable Omnipark?",
                  () =>
                    handleIntegrationToggle("UPDATE", {
                      omnipark: { locationId: "", isEnable: false },
                    }),
                );
              } else {
                setIsEditOmnipark(false);
                setDialogOpenOmnipark(true);
              }
            },
            onEdit: () => {
              setIsEditOmnipark(true);
              setDialogOpenOmnipark(true);
            },
            showEdit:
              get(placeData, "omnipark.isEnable") &&
              get(placeData, "omnipark.locationId"),
          },

          /*************** Other existing settings ***************/

          {
            icon: "label",
            title: "Tagger",
            description:
              "Exposes a license plate validation API for Taggr enforcement officers — confirms whether a vehicle has a valid reservation, permit, or subscription and also provides real-time lot occupancy data by parking code.",
            isEnable: get(placeData, "isTaggerEnable", false),
            onToggle: () =>
              openConfirmation(
                `Tagger ${get(placeData, "isTaggerEnable") ? "Disable" : "Enable"} Confirmation!`,
                `Are you sure you want to ${
                  get(placeData, "isTaggerEnable") ? "Disable" : "Enable"
                } Tagger?`,
                () =>
                  handleIntegrationToggle("UPDATE", {
                    isTaggerEnable: !get(placeData, "isTaggerEnable", false),
                  }),
              ),
          },
          {
            icon: "policy",
            title: "PRRS",
            description:
              "Exposes parking transactions (reservations and permits) to the PRRS platform by lot ID and date range, returning license plates, payment source, and transaction details in the PRRS-required format.",
            isEnable: get(placeData, "isPRRS", false),
            onToggle: () =>
              openConfirmation(
                `PRRS ${get(placeData, "isPRRS") ? "Disable" : "Enable"} Confirmation!`,
                `Are you sure you want to ${
                  get(placeData, "isPRRS") ? "Disable" : "Enable"
                } PRRS?`,
                () =>
                  handleIntegrationToggle("UPDATE", {
                    isPRRS: !get(placeData, "isPRRS", false),
                  }),
              ),
          },
          {
            icon: "security",
            title: "Vanguard",
            description:
              "Exposes an authenticated API for the Vanguard enforcement platform, providing active reservations and permits with license plate, date range, and payment status so Vanguard can identify violators.",
            isEnable: get(placeData, "isVanguardEnable", false),
            onToggle: () =>
              openConfirmation(
                `Vanguard ${
                  get(placeData, "isVanguardEnable") ? "Disable" : "Enable"
                } Confirmation!`,
                `Are you sure you want to ${
                  get(placeData, "isVanguardEnable") ? "Disable" : "Enable"
                } Vanguard?`,
                () =>
                  handleIntegrationToggle("UPDATE", {
                    isVanguardEnable: !get(
                      placeData,
                      "isVanguardEnable",
                      false,
                    ),
                  }),
              ),
          },
          {
            icon: "visibility",
            title: "Wise Sight",
            description:
              "Integrates with Wise Sight ALPR cameras — when a camera event is received with a license plate and parking window, Dock responds with the matching payment, permit, or subscription details to support automated citation decisions.",
            isEnable: get(placeData, "isWiseSightEnable", false),
            onToggle: () =>
              openConfirmation(
                `Wise Sight ${
                  get(placeData, "isWiseSightEnable") ? "Disable" : "Enable"
                } Confirmation!`,
                `Are you sure you want to ${
                  get(placeData, "isWiseSightEnable") ? "Disable" : "Enable"
                } Wise Sight?`,
                () =>
                  handleIntegrationToggle("UPDATE", {
                    isWiseSightEnable: !get(
                      placeData,
                      "isWiseSightEnable",
                      false,
                    ),
                  }),
              ),
          },
          {
            icon: "speed",
            title: "Velocity Parking",
            description:
              "Exposes parking transactions (reservations, permits, and subscriptions) with full financial breakdown — base rate, tax, service fee, and total amount — filtered by location codes and date range for the Velocity Parking platform.",
            isEnable: get(placeData, "isVelocityParkingEnable", false),
            onToggle: () =>
              openConfirmation(
                `Velocity Parking ${
                  get(placeData, "isVelocityParkingEnable")
                    ? "Disable"
                    : "Enable"
                } Confirmation!`,
                `Are you sure you want to ${
                  get(placeData, "isVelocityParkingEnable")
                    ? "Disable"
                    : "Enable"
                } Velocity Parking?`,
                () =>
                  handleIntegrationToggle("UPDATE", {
                    isVelocityParkingEnable: !get(
                      placeData,
                      "isVelocityParkingEnable",
                      false,
                    ),
                  }),
              ),
          },
          {
            icon: "map",
            title: "Mitte",
            description:
              "Exposes parking transactions (reservations, permits, and subscriptions) with full financial details — base rate, tax, service fee, and total amount — filtered by location codes and date range for the Mitte platform.",
            isEnable: get(placeData, "isMitteEnable", false),
            onToggle: () =>
              openConfirmation(
                `Mitte ${
                  get(placeData, "isMitteEnable") ? "Disable" : "Enable"
                } Confirmation!`,
                `Are you sure you want to ${
                  get(placeData, "isMitteEnable") ? "Disable" : "Enable"
                } Mitte?`,
                () =>
                  handleIntegrationToggle("UPDATE", {
                    isMitteEnable: !get(placeData, "isMitteEnable", false),
                  }),
              ),
          },
          {
            icon: "location_city",
            title: "City Park",
            description:
              "Exposes valid license plates (from active permits, subscriptions, and reservations) for a given lot and date range to the City Park platform, returning each record's type, amount, and lot details.",
            isEnable: get(placeData, "isCityParkEnable", false),
            onToggle: () =>
              openConfirmation(
                `City Park ${
                  get(placeData, "isCityParkEnable") ? "Disable" : "Enable"
                } Confirmation!`,
                `Are you sure you want to ${
                  get(placeData, "isCityParkEnable") ? "Disable" : "Enable"
                } City Park?`,
                () =>
                  handleIntegrationToggle("UPDATE", {
                    isCityParkEnable: !get(placeData, "isCityParkEnable", false),
                  }),
              ),
          },
        ].map((integration, index) => (
          <Grid item md={4} sm={6} xs={12} key={index}>
            <IntegrationInfoCard
              color={sidenavColor}
              icon={integration.icon}
              title={integration.title}
              description={integration.description}
              isEnable={integration.isEnable}
              btnText={integration.isEnable ? "Disable" : "Enable"}
              onClick={integration.onToggle}
              btnText2={integration.showEdit ? "Edit" : ""}
              onClick2={integration.showEdit ? integration.onEdit : undefined}
            />
          </Grid>
        ))}
      </Grid>

      {/* Ballparc Modals */}
      <CreateBallparc
        dialogOpen={dialogOpen}
        onClose={() => setDialogOpen(false)}
        ballparcData={placeData?.ballparc}
        isEditBallparc={isEditBallparc}
      />

      {/* ⭐ NEW Omnipark Modals */}
      <CreateOmnipark
        dialogOpen={dialogOpenOmnipark}
        onClose={() => setDialogOpenOmnipark(false)}
        omniparkData={placeData?.omnipark}
        isEditOmnipark={isEditOmnipark}
        onSubmit={(locationId) => {
          handleIntegrationToggle("UPDATE", {
            omnipark: {
              locationId,
              isEnable: true,
            },
          });
          setDialogOpenOmnipark(false);
        }}
      />

      <ConfirmationDialog
        title={confirmationDialog.title}
        description={confirmationDialog.description}
        dialogOpen={confirmationDialog.open}
        onClose={() =>
          setConfirmationDialog((prev) => ({ ...prev, open: false }))
        }
        handelClick={() => {
          confirmationDialog.onConfirm?.();
          setConfirmationDialog((prev) => ({ ...prev, open: false }));
        }}
      />

      <CircularIndeterminate
        type="full"
        size={20}
        text="Please wait.."
        open={isLoading}
      />

      <MDSnackbar
        color={notification.color}
        icon={notification.icon}
        title={notification.title}
        content={notification.content}
        open={notification.show}
        close={handleNotificationClose}
        bgWhite
      />
    </MDBox>
  );
};

export default Integrations;
